import { isRecord } from '@revelio/core/is-record';
import type { CustomRoleTaxonomySelection } from '@revelio/data-access';

import { roleTaxonomySelectionToAiSearchId } from '../../../../shared/selection-lists/utils/roleTaxonomy';
import {
  type Filter,
  FilterId,
  FilterType,
  type SingleValueFilter,
} from '../types/filters';

/**
 * Reads the skill taxonomy id from the active filter map for AI Search API requests.
 * Used by the AI Assistant when requesting filter search.
 */
export function getSkillTaxonomyIdFromActiveFilterMap(
  activeFilterMap: Map<FilterId, Filter>
): number {
  const skillTaxonomyFilter = activeFilterMap.get(FilterId.SKILL_TAXONOMY);
  if (!skillTaxonomyFilter || skillTaxonomyFilter.type !== FilterType.VALUE) {
    return 0;
  }
  const value = (skillTaxonomyFilter as SingleValueFilter<unknown>).value;
  if (!isRecord(value)) return 0;
  const taxonomyId = value['taxonomyId'];
  return typeof taxonomyId === 'number' && Number.isFinite(taxonomyId)
    ? taxonomyId
    : 0;
}

/**
 * Reads the activity taxonomy id from the active filter map for AI Search API requests.
 * Used by the AI Assistant when requesting filter search.
 */
export function getActivityTaxonomyIdFromActiveFilterMap(
  activeFilterMap: Map<FilterId, Filter>
): number {
  const activityTaxonomyFilter = activeFilterMap.get(
    FilterId.ACTIVITY_TAXONOMY
  );
  if (
    !activityTaxonomyFilter ||
    activityTaxonomyFilter.type !== FilterType.VALUE
  ) {
    return 0;
  }
  const value = (activityTaxonomyFilter as SingleValueFilter<unknown>).value;
  if (!isRecord(value)) return 0;
  const taxonomyId = value['taxonomyId'];
  return typeof taxonomyId === 'number' && Number.isFinite(taxonomyId)
    ? taxonomyId
    : 0;
}

/**
 * Reads the role taxonomy id from the active filter map for AI Search API requests.
 * Used by the AI Assistant when requesting filter search.
 */
export function getRoleTaxonomyIdForAiSearch(
  activeFilterMap: Map<FilterId, Filter>
): string {
  const roleTaxonomyFilter = activeFilterMap.get(FilterId.ROLE_TAXONOMY);
  if (!roleTaxonomyFilter || roleTaxonomyFilter.type !== FilterType.VALUE) {
    return 'custom_role_default';
  }
  const value = (roleTaxonomyFilter as SingleValueFilter<unknown>).value;
  if (!isRecord(value)) return 'custom_role_default';
  return roleTaxonomySelectionToAiSearchId(
    value as CustomRoleTaxonomySelection
  );
}
